<template>
  <div class="w-full h-full bg-gray-950">
    <!-- Logged-in view -->
    <Dashboard v-if="user" title="Match Page" no-padding="true">
      <div v-if="matchData && !loading && !isProcessing" class="flex flex-col w-full">
        <MatchHeader
          :matchData="matchData"
          :didUserWin="didUserWin"
          :scoreInfo="scoreInfo"
          :friendlyMapName="friendlyMapName"
          :platformLogo="getPlatformLogo(matchData?.match_platform)"
          :matchTypeLabel="matchTypeLabel"
          :activeTab="activeTab"
          :tabs="availableTabs"
          @updateTab="updateActiveTab"
        >
          <template #actions>
            <button
              v-if="show2DViewer"
              class="py-2.5 px-3 bg-gray-800 text-text-primary text-xs z-50 cursor-pointer hover:bg-gray-700 duration-300 rounded-lg"
              @click="goTo2DViewer()"
            >{{ $t('coach.match_page.two_d_viewer') }}</button>
          </template>
        </MatchHeader>

        <div class="px-4 lg:px-9 py-6">
          <KeepAlive>
            <MatchOverview
              v-if="activeTab === 'overview'"
              :match-data="matchData"
              :active-user-two-lowest="activeUserTwoLowest"
              :active-user-match-data="activeUserMatchData"
              :coach-feedback-enabled="coachFeedbackEnabled"
              :coaching-feedback="matchData?.coaching_feedback"
              :access-level="effectiveAccessLevel(authUser)"
              :viewer-played="viewerPlayed"
              @updateActiveTab="updateActiveTab"
              @update-active-user-two-lowest="activeUserTwoLowest = $event"
              @update-active-user-match-data="activeUserMatchData = $event"
              @openServerModal="openServerModal"
              @feedback-requested="markFeedbackPending(matchData)"
            />
            <MatchStatistics
              v-else-if="activeTab === 'statistics'"
              :match-data="matchData"
              :user-data="userData"
              :selected-user="selectedUser"
              :selected-stat="selectedStat"
              :user-match-history="userMatchHistory"
              @updateActiveTab="updateActiveTab"
              @update-user-data="userData = $event"
              @update-user-match-history="userMatchHistory = $event"
              @update-selected-user="selectedUser = $event"
              @update-selected-stat="selectedStat = $event"
              @openServerModal="openServerModal"
            />
            <MatchRestrat
              v-else-if="activeTab === 'restrat'"
              :match-data="demoData"
              :loading-round="loadingRound"
              :loading-round-success="loadingRoundSuccess"
              @start-demo="startDemo"
            />
          </KeepAlive>
        </div>
      </div>

      <!-- Processing -->
      <div v-else-if="isProcessing" class="w-full h-full flex flex-col text-center items-center justify-center gap-4">
        <svg class="animate-spin h-16 w-16 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <h1 class="text-text-primary text-bold text-xl">{{ $t('coach.match_page.processing_title') }}</h1>
        <h2 class="text-text-secondary w-72">
          {{ $t('coach.match_page.processing_body') }}
        </h2>
      </div>

      <!-- Loading / 404 -->
      <div v-else class="h-full flex justify-center items-center w-full">
        <Loader v-if="loading" />
        <div v-else-if="matchUnavailable" class="max-w-lg text-white text-center">
          <h1 class="text-3xl font-bold text-gray-200 mb-8">{{ $t('coach.match_page.stats_unavailable_title') }}</h1>
          <p class="text-gray-300 mb-6">{{ $t('coach.match_page.stats_unavailable_body') }}</p>
        </div>
        <div v-else class="max-w-lg text-white text-center">
          <h1 class="text-3xl font-bold text-gray-200 mb-8">{{ $t('coach.match_page.not_found_title') }}</h1>
          <i18n-t scope="global" keypath="coach.match_page.not_found_body" tag="p" class="text-gray-300 mb-6">
            <template #link><a href="https://discord.gg/Refrag" target="_blank" class="text-blue-500">{{ $t('coach.match_page.not_found_link') }}</a></template>
          </i18n-t>
        </div>
      </div>

      <StartServerModal :server="serverInfo" :show="showServerModal" :showMapSelector="showMapSelector" @close="showServerModal = false" />
    </Dashboard>

    <!-- Logged-out public view -->
    <div v-else class="flex flex-col h-full items-center w-full bg-gray-950">
      <div class="w-full min-h-[56px] bg-gray-900 border-b border-gray-800 relative gap-4 flex items-center justify-end pr-9">
        <img class="h-6 absolute left-6 top-0 md:left-1/2 md:-translate-x-1/2 translate-y-[16px]" :src="$logo" :alt="$t('coach.alt.refrag_logo')" />
        <router-link to="/login" class="px-3 py-2.5 bg-gray-800 rounded-md text-text-primary text-xs font-medium hover:bg-gray-700">{{ $t('coach.match_page.login') }}</router-link>
        <router-link to="/register" class="px-3 py-2.5 bg-primary-600 rounded-md text-text-primary text-xs font-medium hover:bg-primary-500">{{ $t('coach.match_page.signup') }}</router-link>
      </div>
      <div v-if="matchUnavailable" class="max-w-lg text-white text-center mt-24">
        <h1 class="text-3xl font-bold text-gray-200 mb-8">{{ $t('coach.match_page.stats_unavailable_title') }}</h1>
        <p class="text-gray-300 mb-6">{{ $t('coach.match_page.stats_unavailable_body') }}</p>
      </div>
      <div v-else class="flex flex-col w-full bg-gray-950">
        <MatchHeader
          :matchData="matchData"
          :didUserWin="didUserWin"
          :scoreInfo="scoreInfo"
          :friendlyMapName="friendlyMapName"
          :platformLogo="getPlatformLogo(matchData?.match_platform)"
          :matchTypeLabel="matchTypeLabel"
          :activeTab="activeTab"
          :tabs="availableTabs"
          @updateTab="updateActiveTab"
        />
        <div class="px-9 py-6 max-w-[1200px] mx-auto w-full">
          <KeepAlive>
            <MatchOverview
              v-if="activeTab === 'overview'"
              :match-data="matchData"
              :active-user-two-lowest="activeUserTwoLowest"
              :active-user-match-data="activeUserMatchData"
              :coach-feedback-enabled="coachFeedbackEnabled"
              :coaching-feedback="matchData?.coaching_feedback"
              :access-level="effectiveAccessLevel(authUser)"
              :viewer-played="viewerPlayed"
              @updateActiveTab="updateActiveTab"
              @update-active-user-two-lowest="activeUserTwoLowest = $event"
              @update-active-user-match-data="activeUserMatchData = $event"
              @openServerModal="openServerModal"
              @feedback-requested="markFeedbackPending(matchData)"
            />
            <MatchStatistics
              v-else-if="activeTab === 'statistics'"
              :match-data="matchData"
              :user-data="userData"
              :selected-user="selectedUser"
              :selected-stat="selectedStat"
              :user-match-history="userMatchHistory"
              @updateActiveTab="updateActiveTab"
              @update-user-data="userData = $event"
              @update-selected-user="selectedUser = $event"
              @update-selected-stat="selectedStat = $event"
              @update-user-match-history="userMatchHistory = $event"
              @get-match-data="getMatchData"
              @openServerModal="openServerModal"
            />
            <MatchRestrat
              v-else-if="activeTab === 'restrat'"
              :match-data="demoData"
              :loading-round="loadingRound"
              :loading-round-success="loadingRoundSuccess"
              @start-demo="startDemo"
            />
          </KeepAlive>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed, ref, watch, getCurrentInstance, onMounted, onUnmounted,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import axios from 'axios';

import Dashboard from '../../components/layouts/DashboardLayout.vue';
import MatchHeader from '../../components/Coach/Matches/MatchHeader.vue';
import MatchOverview from '../../components/Coach/Matches/pages/MatchOverview.vue';
import MatchStatistics from '../../components/Coach/Matches/pages/MatchStatistics.vue';
import MatchRestrat from '../../components/Coach/Matches/pages/MatchRestrat.vue';
import StartServerModal from '../../components/StartServerModal.vue';
import Loader from '../../components/General/Loader.vue';
import { compareStatToAverage } from '../../utils/stat_comparison.js';
import { sampleState } from '../../utils/stat_sample.js';
import { useStatDefinitions } from '../../composables/useStatBreakdown.js';
import { rsparseMatchState } from '../../utils/coach_pipeline';
import {
  coachFeedbackEnabledFor, effectiveAccessLevel, viewerOnRoster, applyFeedbackReady, markFeedbackPending,
} from '../../utils/coach_feedback.js';

import steamLogo from '../../assets/icons/steam.svg';
import faceitLogo from '../../assets/icons/faceit.svg';
import refragLogo from '../../assets/logos/refrag-icon.svg';
import renownLogo from '../../assets/logos/renownIcon.png';
import gamersClubLogo from '../../assets/logos/gamers_club_icon.png';

import infoIcon from '../../assets/icons/info-icon.svg';
import statsIcon from '../../assets/icons/stats-icon.svg';
import restratIcon from '../../assets/icons/restrat.svg';

const route = useRoute();
const router = useRouter();
const store = useStore();
const auth = useAuth();
const user = ref(auth.user());
const { t } = useI18n();

// State
const matchData = ref(null);
// The match exists but rsparse has no stats for it; not the same as a 404.
const matchUnavailable = ref(false);
const activeTab = ref(route.query.tab || 'overview');
const loading = ref(true);
const matchProcessing = ref(false);
const selectedUser = ref(null);
const selectedStat = ref(null);
const userData = ref(null);
const userMatchHistory = ref(null);
const activeUserTwoLowest = ref(null);
const activeUserMatchData = ref(null);
const didUserWin = ref(null);
const statDefinitions = useStatDefinitions();

// Demo & server
const demoData = ref(null);
const demoLoading = ref(false);
const loadingRound = ref(false);
const loadingRoundSuccess = ref(false);
const showServerModal = ref(false);
const showMapSelector = ref(false);
const serverInfo = ref(null);
const websocket = ref(null);
const subscription = ref(null);
const matchSubscription = ref(null);
const coachSubscription = ref(null);

const activeServer = computed(() => store.getters.activeServer);

// Derived
const scoreInfo = computed(() => (matchData.value ? [matchData.value.a_score, matchData.value.b_score] : null));

const isMatchTied = computed(() => matchData.value?.a_score === matchData.value?.b_score);
const winningSide = computed(() => {
  if (isMatchTied.value) return null;
  return matchData.value?.a_score > matchData.value?.b_score ? 'a' : 'b';
});

const friendlyMapName = computed(() => {
  if (!matchData.value?.map_name) return 'unknown';
  const name = matchData.value.map_name;
  const parts = name.split('_');
  if (parts.length > 1) {
    return parts.slice(1).map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  }
  return name;
});

const matchTypeLabel = computed(() => {
  const p = matchData.value?.match_platform;
  const type = matchData.value?.match_type;
  if (p === 'renown') return 'Renown';
  if (p === 'gamers_club') return 'Gamers Club';
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
});

const hasRestratData = computed(() => demoReady.value && (isProMatch.value ? canFetchDemo.value : user.value?.access_level >= 2));

// Live user rather than the `user` snapshot above: an upgrade or Steam link
// that landed since setup must not leave the feedback section locked or hidden
// (main.js re-fetches /me after mount and replaces the user object).
const authUser = computed(() => auth.user());
const coachFeedbackEnabled = computed(() => coachFeedbackEnabledFor(authUser.value));
const viewerPlayed = computed(() => viewerOnRoster(matchData.value?.overviews, authUser.value?.steam_id));

const availableTabs = computed(() => {
  const tabs = [
    { key: 'overview', label: t('common.overview'), icon: infoIcon },
    { key: 'statistics', label: t('common.statistics'), icon: statsIcon },
  ];
  if (hasRestratData.value) {
    tabs.push({ key: 'restrat', label: t('coach.match_page.tab_restrat'), icon: restratIcon });
  }
  return tabs;
});

// Only restrat still reads the legacy parse, so only it waits for the demo.
// HLTV demos belong to no team and are open to comp tier.
const isProMatch = computed(() => matchData.value?.match_platform === 'pro');
const demoReady = computed(() => demoData.value?.status === 'ready');
const canFetchDemo = computed(() => !!matchData.value?.team_demo_id && (!isProMatch.value || effectiveAccessLevel(authUser.value) >= 2));
// The viewer reads rsparse and builds rounds on demand, so it waits for nothing.
// Without a team demo it opens by match uuid, behind the backend's MatchesPolicy#viewer? gate.
const OPEN_VIEWER_PLATFORMS = ['refrag', 'renown', 'pro', 'gamers_club'];
const canOpenMatchViewer = computed(() => !!matchData.value && !matchData.value.team_demo_id && !!route.params.id
  && (OPEN_VIEWER_PLATFORMS.includes(matchData.value?.match_platform) || effectiveAccessLevel(authUser.value) >= 2));
const show2DViewer = computed(() => (canFetchDemo.value && !demoData.value?.pov_locked) || canOpenMatchViewer.value);

const isProcessing = computed(() => matchProcessing.value);

const pageTitle = computed(() => (matchData.value?.map_name
  ? t('coach.match_page.title_with_score', { a: scoreInfo.value[0], b: scoreInfo.value[1], map: friendlyMapName.value })
  : t('coach.match_page.title')));

const pageEmbedImage = computed(() => matchData.value?.embed_image || (matchData.value?.map_name ? `https://play.refrag.gg/maps/${matchData.value.map_name}_cs2.png` : null));

useHead({
  title: () => pageTitle.value,
  meta: [
    { name: 'description', content: () => t('coach.match_page.meta_description') },
    { name: 'og:title', content: () => pageTitle.value },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:domain', content: 'play.refrag.gg' },
    { name: 'og:image', content: () => pageEmbedImage.value },
    { name: 'twitter:image', content: () => pageEmbedImage.value },
  ],
});

// Platform logos
const platformLogos = {
  faceit: faceitLogo, refrag: refragLogo, renown: renownLogo, gamers_club: gamersClubLogo,
};
function getPlatformLogo(platform) {
  return platformLogos[platform] || steamLogo;
}

// Navigation
function updateActiveTab(value) {
  if (value === 'highlights') return;
  activeTab.value = value;
  router.push({ query: value === 'overview' ? {} : { tab: value } });
}

function goTo2DViewer() {
  const teamDemoId = matchData.value?.team_demo_id;
  router.push(teamDemoId ? `/demos/${teamDemoId}/review` : `/matches/${route.params.id}/review`);
}

// Server modal
function openServerModal(stat) {
  if (!stat?.recommended_mods?.length) return;
  const modName = stat.recommended_mods[0].name;
  const maps = store.state.modInfo?.mods?.find((m) => m?.name?.toLowerCase() === modName?.toLowerCase())?.cs_maps || [];
  serverInfo.value = {
    mode: modName,
    cs_map: maps.find((map) => map.name === matchData.value?.map_name),
    time_to_kill: activeUserMatchData.value?.time_to_kill_average?.value || null,
  };
  showMapSelector.value = true;
  showServerModal.value = true;
}

// Demo & restrat
function startDemo(roundId) {
  loadingRound.value = true;
  if (!activeServer.value?.id) {
    serverInfo.value = {
      mode: 'Restrat',
      cs_map: matchData.value?.cs_map,
      content: { id: matchData.value?.id, round_id: roundId },
    };
    showServerModal.value = true;
    return;
  }
  axios.post(`/cs_servers/${activeServer.value.id}/exec_server_command`, null, {
    params: { command: 'load_restrat', value: `${demoData.value?.id}, ${roundId}` },
    headers: { 'X-TEAM-ID': store.getters.activeTeam.id },
  }).then(() => {
    loadingRound.value = false;
    loadingRoundSuccess.value = true;
    setTimeout(() => { loadingRoundSuccess.value = false; }, 3000);
  }).catch(() => {
    loadingRound.value = false;
    store.commit('showMessage', { messageType: 'error', messageText: t('coach.match_page.server_unreachable') });
    setTimeout(() => store.commit('hideMessage'), 3000);
  });
}

function getDemoData() {
  if (!matchData.value?.team_demo_id) return;
  demoLoading.value = true;
  // A viewer with no team has no active team, and HLTV demos do not need one.
  const teamId = store.getters.activeTeam?.id;
  axios.get(`/demos/${matchData.value.team_demo_id}`, {
    headers: teamId ? { 'X-TEAM-ID': teamId } : {},
  }).then((response) => {
    demoData.value = response.data;
    demoLoading.value = false;
    subscribeToDemo();
  }).catch(() => { demoLoading.value = false; });
}

function subscribeToDemo() {
  if (!matchData.value?.team_demo_id) return;
  const cable = getCurrentInstance().appContext.config.globalProperties.$cable;
  if (!websocket.value) websocket.value = cable.connect();
  if (!subscription.value) {
    subscription.value = websocket.value.subscriptions.create(
      { channel: 'DemoChannel', id: matchData.value.team_demo_id },
      { received: () => getDemoData() },
    );
  }
}

function subscribeToMatch() {
  const matchId = route.params.id;
  if (!matchId) return;
  const cable = getCurrentInstance().appContext.config.globalProperties.$cable;
  if (!websocket.value) websocket.value = cable.connect();
  if (!matchSubscription.value) {
    matchSubscription.value = websocket.value.subscriptions.create(
      { channel: 'MatchChannel', id: matchId },
      { received: () => getMatchData(false) },
    );
  }
}

// Coaching feedback is generated asynchronously and takes ~7-15s, so it is
// usually NOT in the response this page first rendered from: the request is
// fired when the match finalises, and the player often opens the page before
// (or long after) it lands. Without this, the only way to see it was a reload.
//
// CoachChannel is per USER, not per match, because feedback is private to the
// player it is about. The broadcast carries no prose, only which match it is
// for, so we refetch and the server hands back this viewer's own feedback
// through the same authorised endpoint as the initial load.
function subscribeToCoachFeedback() {
  const cable = getCurrentInstance().appContext.config.globalProperties.$cable;
  if (!websocket.value) websocket.value = cable.connect();
  if (coachSubscription.value) return;

  coachSubscription.value = websocket.value.subscriptions.create(
    { channel: 'CoachChannel' },
    {
      received: (data) => {
        // The stream is per user, so it also carries feedback for matches this
        // page is not showing. Ignore those rather than refetching blindly.
        if (data?.type !== 'feedback_ready') return;
        if (String(data.match_id) !== String(route.params.id)) return;
        if (applyFeedbackReady(matchData.value, data.status)) getMatchData(false);
      },
    },
  );
}

// Match data
function getMatchData(shouldLoad = true) {
  if (shouldLoad) loading.value = true;

  axios.get(`/coach/matches/${route.params.id}`).then((response) => {
    // An empty overviews list is either "still parsing" or "rsparse never
    // processed this match". See rsparseMatchState — everything below reads
    // player rows out of overviews.
    const state = rsparseMatchState(response.data);
    matchUnavailable.value = state === 'unavailable';

    // Only ever flipped from a response, never at the top of the function: the
    // MatchChannel refetch runs with loading false, so clearing it up front left
    // the "404 - Match Not Found" branch on screen until the response landed.
    matchProcessing.value = state === 'processing';

    if (state !== 'ready') {
      matchData.value = null;
      return;
    }

    matchData.value = response.data;

    if (!selectedUser.value) {
      const steamId = user.value?.steam_id;
      selectedUser.value = response.data.overviews.find((p) => p.steam_id == steamId) || response.data.overviews[0];
    }

    updateUserWinInfo();
    if (matchData.value.match_platform !== 'refrag') {
      setActiveUserData();
      calculateActiveUserTwoLowest();
    }

    if (canFetchDemo.value) getDemoData();
  }).catch((err) => {
    console.error('Failed to load match:', err);
  }).finally(() => {
    loading.value = false;
  });
}

function updateUserWinInfo() {
  const steamId = user.value?.steam_id;
  const userOverview = matchData.value.overviews.find((p) => p.steam_id == steamId);
  didUserWin.value = userOverview ? userOverview.side === winningSide.value : null;
}

function setActiveUserData() {
  const steamId = user.value?.steam_id;
  const userOverview = matchData.value.overviews.find((p) => p.steam_id == steamId);
  activeUserMatchData.value = userOverview || null;
  userData.value = userOverview || null;
}

const WORSE_WHEN_HIGHER = new Set([
  'time_to_kill_average', 'time_to_damage_average', 'crosshair_placement_average',
  'opening_fails', 'trade_kill_fails', 'traded_death_fails',
  'util_flash_friends_flashed', 'util_lost_cost',
]);

function calculateActiveUserTwoLowest() {
  if (!activeUserMatchData.value) {
    activeUserTwoLowest.value = [];
    return;
  }

  const stats = activeUserMatchData.value;
  const differences = [];

  for (const [key, stat] of Object.entries(stats)) {
    if (!stat || typeof stat !== 'object' || !stat.recommended_mods?.length) continue;
    // Too few attempts: not one of the two lowest either (same floor as the Statistics tab).
    if (sampleState(key, stats, statDefinitions.value)?.belowMin) continue;

    const isInverted = WORSE_WHEN_HIGHER.has(key);
    // Skipped when there is no peer average to compare against
    const higherIsBetter = !isInverted;
    const comparison = compareStatToAverage(stat.value, stat.avg, higherIsBetter);

    if (!comparison) continue;

    const { difference, percentDifference } = comparison;

    differences.push({
      title: key,
      friendly_title: key,
      value: stat.value,
      average: stat.avg,
      difference: difference.toFixed(2),
      percentDifference: percentDifference.toFixed(2),
      unit_type: ['time_to_kill_average', 'time_to_damage_average', 'average_enemy_flash_time'].includes(key)
        ? 'seconds'
        : stat.unit_type,
      is_ascending: isInverted,
      recommended_mods: stat.recommended_mods,
      rating_range_average_value: stat.avg,
    });
  }

  differences.sort((a, b) => a.percentDifference - b.percentDifference);
  activeUserTwoLowest.value = differences.filter((s) => s.percentDifference < 0).slice(0, 2);
}

// The floors usually land after the match does.
watch(statDefinitions, () => { if (activeUserMatchData.value) calculateActiveUserTwoLowest(); });

// Init
if (route.query.tab) updateActiveTab(route.query.tab);
getMatchData();

onMounted(() => {
  subscribeToMatch();
  if (user.value) subscribeToCoachFeedback();
});

onUnmounted(() => {
  matchSubscription.value?.unsubscribe();
  subscription.value?.unsubscribe();
  coachSubscription.value?.unsubscribe();
});
</script>
